import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { errMsg } from "@/lib/community";

export function useAdminData(path, deps = []) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const load = useCallback(async () => {
    try {
      const { data } = await api.get(path);
      setData(data);
    } catch (e) {
      if (e?.response?.status === 401) {
        localStorage.removeItem("jenova_admin_token");
        navigate("/admin/login");
      } else toast.error(errMsg(e));
    }
    // eslint-disable-next-line
  }, [path, ...deps]);
  useEffect(() => {
    load();
  }, [load]);
  return [data, load, setData];
}

export async function adminAct(fn, success) {
  try {
    const r = await fn();
    if (success) toast.success(success);
    return r?.data ?? true;
  } catch (e) {
    toast.error(errMsg(e));
    return null;
  }
}

export function Field({ label, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="block text-[10px] uppercase tracking-[0.2em] text-[#7A6A62] mb-1.5">{label}</span>
      {children}
    </label>
  );
}

export const inputCls = "w-full border border-[#E8DCCB] bg-white rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#2B2522]";

export function NSelect({ value, onChange, options, testId, className = "" }) {
  return (
    <select value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={`${inputCls} ${className}`} data-testid={testId}>
      {options.map((o) => (typeof o === "string" ? <option key={o} value={o}>{o}</option> : <option key={o.value} value={o.value}>{o.label}</option>))}
    </select>
  );
}

const BADGE = {
  approved: "bg-emerald-50 text-emerald-700", published: "bg-emerald-50 text-emerald-700", confirmed: "bg-emerald-50 text-emerald-700",
  sent: "bg-emerald-50 text-emerald-700", pending: "bg-amber-50 text-amber-700", scheduled: "bg-sky-50 text-sky-700",
  waitlisted: "bg-amber-50 text-amber-700", draft: "bg-stone-100 text-stone-600", rejected: "bg-rose-50 text-rose-700",
  cancelled: "bg-rose-50 text-rose-700", suspended: "bg-rose-50 text-rose-700", revoked: "bg-rose-50 text-rose-700",
  failed: "bg-rose-50 text-rose-700", none: "bg-stone-100 text-stone-500",
  live: "bg-emerald-50 text-emerald-700", ended: "bg-stone-100 text-stone-500",
};

export function Badge({ s, testId }) {
  return (
    <span data-testid={testId} className={`inline-block px-2.5 py-0.5 rounded-full text-[10px] uppercase tracking-[0.15em] ${BADGE[s] || "bg-stone-100 text-stone-600"}`}>
      {s || "none"}
    </span>
  );
}

export function Panel({ title, action, children, testId }) {
  return (
    <section className="bg-white border border-[#E8DCCB] rounded-lg p-5 md:p-7" data-testid={testId}>
      {(title || action) && (
        <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
          {title && <h3 className="font-serif text-2xl">{title}</h3>}
          {action}
        </div>
      )}
      {children}
    </section>
  );
}

export const btn = "inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs tracking-wide transition-colors disabled:opacity-50";
export const btnDark = `${btn} bg-[#2B2522] text-white hover:bg-[#4A3F3A]`;
export const btnLine = `${btn} border border-[#E8DCCB] hover:border-[#2B2522] bg-white`;
export const btnDanger = `${btn} border border-rose-200 text-rose-700 hover:bg-rose-50`;
